import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import {
  Loader2, Send, AlertCircle, AlertTriangle, CheckCircle2, Mail, FileText, Paperclip, RefreshCw, Settings2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../foundation/altzero_base/base/components/ui/dialog';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { BillReportScreen } from './BillReportScreen';
import { renderReportHtmlBlob, renderReportPdfBlob } from '../../utils/pdfUtils';
import { HtmlBodyEditor } from '../common/HtmlBodyEditor';
import { composeReportEmail, EMAIL_RE } from '../../lib/bulkSend';
import {
  bvReportService,
  type BvReportContext,
  type BvBillsMailbox,
  type BvReportSend,
  type BvEmailTemplate,
} from '../../services/bvReportService';
import { fmtDate } from '../../lib/reportFormulas';
import type { BvBill, ShadowBillResult, ActualBillResult } from '../../types/billTypes';

type SendBillReportDialogProps = {
  open: boolean;
  onClose: () => void;
  bill: BvBill;
  shadowResult: ShadowBillResult;
  actualResult: ActualBillResult;
  /** GST switched out of the comparison — the report then runs ex-GST. */
  gstExcluded?: boolean;
};

function parseEmails(raw: string): string[] {
  return raw
    .split(/[,;\n]/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

function invalidEmails(raw: string): string[] {
  return parseEmails(raw).filter((e) => !EMAIL_RE.test(e));
}

function fmtWhen(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Send the bill validation report to the client.
 *
 * The right-hand pane is not a preview *of* the report — it **is** the report,
 * the same `BillReportScreen` component the adviser reviews everywhere else. On
 * send it is captured to PDF by the same function behind the Download PDF
 * button, and those bytes are what the client receives. There is no second
 * rendering of the report anywhere, so the emailed document cannot disagree with
 * the one that was checked.
 */
export const SendBillReportDialog = ({
  open, onClose, bill, shadowResult, actualResult, gstExcluded,
}: SendBillReportDialogProps) => {
  const billId = bill.id;
  const nmi = bill.site?.nmi ?? '—';
  const clientName = bill.site?.client?.name ?? '—';
  const periodLabel = `${fmtDate(bill.bill_from)} – ${fmtDate(bill.bill_to)}`;

  const reportRef = useRef<HTMLDivElement>(null);

  const [emailTemplates, setEmailTemplates] = useState<BvEmailTemplate[]>([]);
  const [emailTemplateId, setEmailTemplateId] = useState<string | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const [mailboxes, setMailboxes] = useState<BvBillsMailbox[]>([]);
  const [accountId, setAccountId] = useState<string | null>(null);

  const [context, setContext] = useState<BvReportContext | null>(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  // Off by default: attaching the retailer's own invoice is a deliberate choice
  // for a dispute, not something that should ride along on every report.
  const [attachInvoice, setAttachInvoice] = useState(false);

  const [sending, setSending] = useState(false);
  const [sendStage, setSendStage] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sentFrom, setSentFrom] = useState<string | null>(null);

  const [history, setHistory] = useState<BvReportSend[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ── Reset whenever the dialog closes, or the bill changes ─────────────────
  // The Reports page reuses a single instance of this dialog across every bill
  // in the list. Without clearing the composed fields, opening it for one client
  // and then another would carry the first client's recipients and subject
  // across. Losing a draft on close is the lesser problem by a wide margin.
  useEffect(() => {
    setContext(null);
    setContextError(null);
    setSendError(null);
    setSentFrom(null);
    setSending(false);
    setSendStage(null);
    setTo('');
    setCc('');
    setSubject('');
    setBody('');
    // Back off too — ticking it for one client must not silently attach the next
    // client's invoice when the dialog is reused for a different bill.
    setAttachInvoice(false);
  }, [open, billId]);

  // ── Email templates + mailboxes ───────────────────────────────────────────
  // Templates are also reloadable on demand: the manage screen opens in another
  // tab, so a template added there has to be pullable without losing this draft.
  const loadTemplates = useCallback(async (selectDefault: boolean) => {
    setTemplatesLoading(true);
    const res = await bvReportService.listEmailTemplates();
    if (res.success) {
      const list = res.data ?? [];
      setEmailTemplates(list);
      if (selectDefault || !list.some((t) => t.id === emailTemplateId)) {
        setEmailTemplateId(list.find((t) => t.is_default)?.id ?? list[0]?.id ?? null);
      }
    } else {
      setSendError(res.error ?? 'Could not load the email templates');
    }
    setTemplatesLoading(false);
  }, [emailTemplateId]);

  useEffect(() => {
    if (!open) return;
    let mounted = true;

    void (async () => {
      const [tplRes, mailRes] = await Promise.all([
        bvReportService.listEmailTemplates(),
        bvReportService.listMailboxes(),
      ]);
      if (!mounted) return;

      if (tplRes.success) {
        const list = tplRes.data ?? [];
        setEmailTemplates(list);
        setEmailTemplateId(list.find((t) => t.is_default)?.id ?? list[0]?.id ?? null);
      } else {
        setSendError(tplRes.error ?? 'Could not load the email templates');
      }

      if (mailRes.success) {
        const list = mailRes.data ?? [];
        setMailboxes(list);
        setAccountId(list[0]?.id ?? null);
      } else {
        setSendError(mailRes.error ?? 'Could not load the bills mailbox');
      }
    })();

    return () => { mounted = false; };
  }, [open]);

  // ── Send history for this bill ────────────────────────────────────────────
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    const res = await bvReportService.history(billId);
    if (res.success) setHistory(res.data ?? []);
    setHistoryLoading(false);
  }, [billId]);

  useEffect(() => {
    if (!open) return;
    void loadHistory();
  }, [open, loadHistory]);

  // ── The bill's figures, for filling the covering email ────────────────────
  useEffect(() => {
    if (!open) return;
    let mounted = true;

    void (async () => {
      setContextLoading(true);
      setContextError(null);
      const res = await bvReportService.context(billId);
      if (!mounted) return;

      if (res.success && res.data) {
        setContext(res.data);
        setTo((prev) => prev || res.data!.suggestedRecipients.map((r) => r.email).join(', '));
      } else {
        setContext(null);
        setContextError(res.error ?? 'Could not read this bill’s figures');
      }
      setContextLoading(false);
    })();

    return () => { mounted = false; };
  }, [open, billId]);

  // ── Compose the covering email from the chosen template ───────────────────
  // Applied once per (bill, template) pair, tracked by ref so that edits made
  // after the template is applied survive an unrelated re-render — while picking
  // a different template still replaces the draft, which is the point of it.
  const appliedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open || !context) return;

    const key = `${billId}:${emailTemplateId ?? 'fallback'}`;
    if (appliedRef.current === key) return;
    appliedRef.current = key;

    const template = emailTemplates.find((t) => t.id === emailTemplateId);
    const composed = composeReportEmail(template, context.tokens);
    setSubject(composed.subject);
    setBody(composed.bodyHtml);
  }, [open, context, emailTemplateId, emailTemplates, billId]);

  const badEmails = [...invalidEmails(to), ...invalidEmails(cc)];
  const recipients = parseEmails(to).filter((e) => EMAIL_RE.test(e));
  const canSend =
    !!context && !contextLoading && !sending &&
    recipients.length > 0 && badEmails.length === 0 &&
    subject.trim().length > 0 && mailboxes.length > 0;

  const handleSend = async () => {
    setSending(true);
    setSendError(null);
    try {
      // Capture the report exactly as rendered in the pane beside the form.
      setSendStage('Capturing the report…');
      const target = (reportRef.current?.querySelector('[data-pdf-target]') ?? reportRef.current) as HTMLElement | null;
      if (!target) {
        setSendError('The report is not rendered yet — wait for it to appear and try again.');
        return;
      }
      // One file, the one Super Admin chose — PDF and HTML together run past
      // the upload limit.
      const format = await bvReportService.attachmentFormat();
      const file = format === 'html'
        ? await renderReportHtmlBlob(target, subject.trim())
        : await renderReportPdfBlob(target);

      setSendStage('Sending…');
      const res = await bvReportService.send({
        billId,
        accountId,
        recipients,
        cc: parseEmails(cc).filter((e) => EMAIL_RE.test(e)),
        subject: subject.trim(),
        bodyHtml: body,
        attachInvoice,
        report: { format, file },
      });

      if (!res.success || !res.data) {
        setSendError(res.error ?? 'The report could not be sent');
        await loadHistory();
        return;
      }

      setSentFrom(res.data.sentFrom);
      await loadHistory();
    } catch (error: unknown) {
      setSendError(error instanceof Error ? error.message : 'The report could not be sent');
    } finally {
      setSending(false);
      setSendStage(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      {/* Full screen: the report pane is an A4 document and the compose column
          sits beside it — anything smaller makes both cramped. */}
      <DialogContent data-testid="send-report-dialog" className="!left-0 !top-0 !translate-x-0 !translate-y-0 w-screen h-screen max-w-none rounded-none border-0 flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4" />
            Send Bill Validation Report — {clientName} · {nmi} · {periodLabel}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex min-h-0">

          {/* ── Compose ─────────────────────────────────────────────────── */}
          <div className="w-[520px] shrink-0 border-r overflow-y-auto p-5 space-y-4">

            {sentFrom && (
              <div className="flex gap-2 items-start rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-800">
                  <p className="font-semibold">Report sent</p>
                  <p className="text-xs mt-0.5">From {sentFrom} to {recipients.join(', ')}</p>
                </div>
              </div>
            )}

            {sendError && (
              <div data-testid="send-report-error" className="flex gap-2 items-start rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
                <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">
                  <p className="font-semibold">Send failed</p>
                  <p className="text-xs mt-0.5 break-words">{sendError}</p>
                </div>
              </div>
            )}

            {mailboxes.length === 0 && (
              <div className="flex gap-2 items-start rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  No active bills mailbox is connected. Connect one under Bill Validation → Inbox
                  before sending reports — the tender mailbox is deliberately not offered here,
                  because client replies would land in the tender inbox.
                </p>
              </div>
            )}

            <Field label="Email template">
              <div className="flex items-center gap-1.5">
                <select
                  value={emailTemplateId ?? ''}
                  onChange={(e) => setEmailTemplateId(e.target.value || null)}
                  className="flex-1 min-w-0 text-sm border border-gray-200 rounded-md px-2.5 py-2 bg-white"
                >
                  {emailTemplates.length === 0 && <option value="">Standard bill validation email</option>}
                  {emailTemplates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.is_default ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => void loadTemplates(false)}
                  title="Reload templates"
                  className="p-2 text-gray-400 hover:text-gray-700 shrink-0"
                >
                  <RefreshCw className={cn('h-3.5 w-3.5', templatesLoading && 'animate-spin')} />
                </button>
                {/* Opened in a new tab on purpose: this dialog is a modal over the
                    report, and navigating away from it would throw away the draft. */}
                <button
                  type="button"
                  onClick={() => window.open('/bill-validation/email-templates', '_blank', 'noopener')}
                  className="flex items-center gap-1 px-2 py-2 text-[11px] font-medium text-blue-600 hover:text-blue-700 shrink-0"
                >
                  <Settings2 className="h-3.5 w-3.5" /> Manage
                </button>
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                {emailTemplates.length === 0
                  ? 'No saved templates yet — a standard email is used. Manage opens the template editor in a new tab.'
                  : 'Fills the subject and message below with this bill’s figures. Edit either before sending.'}
              </p>
            </Field>

            <Field label="Send from">
              <select
                value={accountId ?? ''}
                onChange={(e) => setAccountId(e.target.value || null)}
                disabled={mailboxes.length === 0}
                className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-2 bg-white disabled:bg-gray-50"
              >
                {mailboxes.map((m) => <option key={m.id} value={m.id}>{m.email}</option>)}
                {mailboxes.length === 0 && <option value="">No bills mailbox connected</option>}
              </select>
              {mailboxes.length > 0 && (
                <p className="text-[11px] text-gray-400 mt-1">
                  Replies come back to this mailbox, where the bill validation poller reads them.
                </p>
              )}
            </Field>

            <Field label="To">
              <textarea
                value={to}
                onChange={(e) => setTo(e.target.value)}
                rows={2}
                placeholder="client@example.com"
                className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-2 resize-none"
              />
            </Field>

            <Field label="CC">
              <textarea
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                rows={1}
                className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-2 resize-none"
              />
            </Field>

            {badEmails.length > 0 && (
              <p className="text-xs text-red-600">
                Not a valid email address: {badEmails.join(', ')}
              </p>
            )}

            <Field label="Subject">
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-2"
              />
            </Field>

            <Field label="Message">
              <HtmlBodyEditor value={body} onChange={setBody} heightClass="h-[320px]" />
              <p className="text-[11px] text-gray-400 mt-1">
                <strong>Design</strong> edits the message as the client will see it;
                <strong> HTML</strong> shows the source. Both edit the same message.
              </p>
            </Field>

            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={attachInvoice}
                onChange={(e) => setAttachInvoice(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" /> Attach the original invoice</span>
                <span className="block text-[11px] text-gray-400 mt-0.5">
                  Useful in a dispute — the client can put our figures next to the retailer's own document.
                </span>
              </span>
            </label>

            <Button data-testid="send-report-submit" onClick={() => void handleSend()} disabled={!canSend} className="w-full gap-2">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sendStage ?? 'Send report'}
            </Button>

            {/* ── Send history ──────────────────────────────────────────── */}
            <div className="pt-2 border-t">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Send history</p>
                <button
                  type="button"
                  onClick={() => void loadHistory()}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Refresh send history"
                >
                  <RefreshCw className={cn('h-3.5 w-3.5', historyLoading && 'animate-spin')} />
                </button>
              </div>

              {historyLoading && history.length === 0 ? (
                <p className="text-xs text-gray-400">Loading…</p>
              ) : history.length === 0 ? (
                <p className="text-xs text-gray-400">This report has not been sent to the client yet.</p>
              ) : (
                <ul className="space-y-2">
                  {history.map((h) => (
                    <li key={h.id} className="text-xs border border-gray-100 rounded-md px-2.5 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className={cn(
                          'font-semibold uppercase tracking-wide text-[10px] px-1.5 py-0.5 rounded',
                          h.status === 'sent'   ? 'bg-emerald-100 text-emerald-700'
                          : h.status === 'failed' ? 'bg-red-100 text-red-700'
                          :                         'bg-gray-100 text-gray-600',
                        )}>
                          {h.status}
                        </span>
                        <span className="text-gray-400">{fmtWhen(h.sent_at ?? h.created_at)}</span>
                      </div>
                      <p className="text-gray-600 mt-1 break-words">{h.recipient_emails.join(', ')}</p>
                      {h.account?.email && <p className="text-gray-400 mt-0.5">from {h.account.email}</p>}
                      {h.status === 'failed' && h.error && (
                        <p className="text-red-600 mt-1 break-words">{h.error}</p>
                      )}
                      {h.status === 'sent' && h.pdf_storage_path && (
                        <button
                          type="button"
                          onClick={() => void openStoredPdf(h.id)}
                          className="mt-1 text-blue-600 hover:underline flex items-center gap-1"
                        >
                          <FileText className="h-3 w-3" /> View the PDF that was sent
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* ── The report itself ───────────────────────────────────────── */}
          <div className="flex-1 min-w-0 flex flex-col bg-gray-100">
            <div className="px-4 py-2 border-b bg-white flex items-center gap-3 flex-wrap">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Report</p>
              <p className="text-[11px] text-gray-400">
                This is the document that gets attached — captured from exactly what you see here.
              </p>
            </div>

            {contextError && (
              <Banner tone="red" icon={<AlertCircle className="h-4 w-4" />}>
                {contextError}
              </Banner>
            )}

            {context && context.meta.matchWarning && (
              <Banner tone="amber" icon={<AlertTriangle className="h-4 w-4" />}>
                {context.meta.matchWarning}
              </Banner>
            )}

            {context && context.meta.warnings.length > 0 && (
              <Banner tone="amber" icon={<AlertTriangle className="h-4 w-4" />}>
                <span className="font-semibold">
                  The expected invoice was calculated with {context.meta.warnings.length} warning
                  {context.meta.warnings.length === 1 ? '' : 's'}:
                </span>{' '}
                {context.meta.warnings.join(' · ')}
              </Banner>
            )}

            <div className="flex-1 min-h-0 overflow-y-auto">
              <div ref={reportRef}>
                <BillReportScreen
                  bill={bill}
                  shadowResult={shadowResult}
                  actualResult={actualResult}
                  gstExcluded={gstExcluded}
                />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

async function openStoredPdf(sendId: string): Promise<void> {
  const res = await bvReportService.pdfLink(sendId);
  if (res.success && res.data?.url) window.open(res.data.url, '_blank', 'noopener');
}

type FieldProps = { label: string; children: ReactNode };

const Field = ({ label, children }: FieldProps) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

type BannerProps = { tone: 'amber' | 'red'; icon: ReactNode; children: ReactNode };

const Banner = ({ tone, icon, children }: BannerProps) => (
  <div className={cn(
    'flex gap-2 items-start px-4 py-2 text-xs border-b',
    tone === 'amber' ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-red-50 border-red-200 text-red-900',
  )}>
    <span className="shrink-0 mt-0.5">{icon}</span>
    <span className="break-words">{children}</span>
  </div>
);
